<template lang="pug">
.pie(:class="pieChartWrapClass")
  .pie__header
    .pie__label {{ legend?.title }}

  .pie__content
    .pie__content-chart
      .pie__header__text(v-if="legend?.result")
        span.pie__percentage {{ legend.result.value }}
        span.pie__description {{ legend.result.label }}
      .pie__chart-container(:style="{ width: `${height}px`, height: `${height}px` }")
        apexchart(
          type="donut",
          :height="height",
          :width="height",
          :options="chartOptions",
          :series="data",
          v-if="!allValuesZero"
        )
        span(v-else) {{ gridLocales?.hidden_message }}
    .pie__legend
      .pie__legend__by-params(
        v-for="item in legend.text",
        :key="item.title",
        :style="{ width: `calc(100% / ${legend.text.length})` }"
      )
        .pie__legend__title {{ item.title }}
        .pie__legend__items
          .pie__legend__item(
            v-for="legendItem in getLegendItemsByType(item.type)",
            :key="`${legendItem.type}_${legendItem.label}`"
          )
            .pie__legend__item__circle(:style="`background-color: ${legendItem.color}`")
            .pie__legend__item__content 
              .pie__legend__item__label {{ legendItem.label }}
              .pie__legend__item__value 
                | {{ legendItem.percent_value }}
                span {{ `(${legendItem.value})` }}
        .pie__legend__separator
        .pie__legend__all
          .pie__legend__all__item(v-for="resultItem in getResultItemsByType(item.type)", :key="resultItem.label")
            .pie__legend__all__item__label {{ resultItem.label }}
            .pie__legend__all__item__value
              | {{ resultItem.percent_value }}
              span {{ `(${resultItem.value})` }}
</template>

<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { gridLocales } from "@/services/useLocales";

const props = defineProps({
  /**
   * Дата самого чарта. Имеет несколько обязательных ключей: data, legend, items, result. Пример:
   * {
   *  data: [1, 1],
   *  items: [
        {
          "label": 6,
          "value": 1,
          "percent_value": "50%",
          "color": "#FA7167",
          "type": "pie_part"
        },
        {
          "label": 7,
          "value": 1,
          "percent_value": "50%",
          "color": "#B5BBC9",
          "type": "pie_part"
        },
      ],
      legend: {
        text: [{
          title: "Количество заявок у исполнителя",
          type: "pie_part"
        }],
        title: "Распределение заявок между Исполнитель1 и Исполнитель2",
      },
      result: [
        {
          value: 2,
          label: "Всего",
          percent_value: "100%",
          type: "pie_part",
        },
      ],
   * }
   */
  chartData: { type: Object, required: true },
  /**
   * Высота чарта. Пример: 200
   */
  height: { type: Number, default: () => 200 },
  /**
   * Флаг переноса легенды под чат
   */
  wrapLegend: { type: Boolean, default: () => false },
});

const chartOptions = ref({
  chart: {
    type: "pie",
    toolbar: {
      show: false,
    },
    events: {
      // Функция на слушатель наведение курсора на дугу графика
      dataPointMouseEnter: ({ target }, ctx) => {
        // В дуге графика проверяем есть ли у него аттрибут что на него нажали
        if (target.getAttribute("data:pieClicked") !== "true") {
          // Если не нажали, берем нужный индекс дуги
          const sliceIndex = parseInt(target.attributes.j.value);
          // Имитируем клик по ней чтоб она увеличилась в размерах
          ctx.pie.pieClicked(sliceIndex);
        }
      },
      // Обратная ситуация на слушатель уведение курсора с дуги
      dataPointMouseLeave: ({ target }, ctx) => {
        if (target.getAttribute("data:pieClicked") === "true") {
          const sliceIndex = parseInt(target.attributes.j.value);
          ctx.pie.pieClicked(sliceIndex);
        }
      },
      // При клике на дугу имитируем клик по дуге чтоб повесить аттрибут "data:pieClicked"
      click: ({ target }, ctx) => {
        const sliceIndex = parseInt(target.attributes.j.value);
        ctx.pie.pieClicked(sliceIndex);
      },
    },
  },
  // Запрещаем дефолтные состояния
  states: {
    hover: {
      filter: { type: "none" },
    },
    active: {
      filter: { type: "none" },
    },
  },
  tooltip: {
    enabled: true,
    custom: function (opts) {
      // candlestick standard
      const data = opts.series[opts.seriesIndex];
      const color = opts.w.config.colors[opts.seriesIndex];
      let text = `<div class='column-chart__tooltip' style='background-color: ${color}BF;', background'><span class='column-chart__tooltip__label'>${data} ₽</span></div>`;

      return text;
    },
  },
  plotOptions: {
    pie: {
      dataLabels: {
        offset: -2,
      },
      donut: {
        size: "55%",
        background: "transparent",
      },
    },
  },
  colors: [],
  legend: {
    show: false,
  },
});
const allValuesZero = ref(false);

const legend = computed(() => props.chartData?.legend);
const data = computed(() => props.chartData?.data);
const result = computed(() => props.chartData?.result);
const items = computed(() => props.chartData?.items);
const wrapLegend = computed(() => props.wrapLegend);

const pieChartWrapClass = computed(() => (wrapLegend.value ? "wrap" : ""));

const setChartParams = () => {
  chartOptions.value.colors = items.value.map(item => item.color);
  const nonEmptyValues = data.value.filter(el => el !== 0);
  allValuesZero.value = nonEmptyValues.length === 0;
};

const getLegendItemsByType = type => {
  return items.value.filter(item => item.type === type);
};

const getResultItemsByType = type => {
  return result.value.filter(item => item.type === type);
};

onBeforeMount(() => {
  setChartParams();
});
</script>

<style lang="scss">
.pie {
  width: 100%;
  background-color: var(--expenses-content-background-color);
  border-radius: 20px;
  padding: 24px;

  &__header {
    &__text {
      margin-bottom: 20px;
    }
  }

  &__label {
    color: var(--expenses-main-text-color);
    font-size: 20px;

    margin-left: 0;
  }

  &__percentage {
    color: var(--expenses-main-text-color);
    font-weight: 600;
    font-size: 16px;
    display: block;
  }

  &__description {
    font-size: 14px;
    font-weight: 500;
    color: #aeb5c4;
  }

  &__content {
    height: calc(100% - 48px);

    display: flex;
    justify-content: space-between;
    gap: 32px;
    margin-top: 10px;
  }

  &__chart-container {
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 50%;
    box-shadow: var(--expenses-pie-chart-box-shadow);

    span {
      color: var(--expenses-main-text-color);
    }
  }

  &__legend {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    flex-basis: min-content;
    justify-content: space-between;
    gap: 15px;

    &__title {
      font-size: 16px;
      margin-bottom: 12px;
      color: var(--expenses-main-text-color);
    }

    &__separator {
      border: 0.5px solid #b5bbc9;
      margin: 4px 0;
    }

    &__by-params {
      display: flex;
      flex-direction: column;
      gap: 5px;
      padding: 20px;
      background-color: var(--expenses-pie-chart-legend-background-color);
      border-radius: 17px;
    }

    &__all {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin-left: 13px;

      &__item {
        font-size: 14px;

        &__label {
          font-weight: 500;
          color: #a5a5a5;
        }

        &__value {
          font-weight: 500;
          font-size: 16px;
          color: var(--expenses-main-text-color);

          span {
            font-size: 14px;
            margin-left: 3px;
          }
        }
      }
    }

    &__items {
      overflow: auto;
    }

    &__item {
      min-width: 45%;

      display: flex;
      gap: 5px;

      &__circle {
        width: 8px;
        height: 8px;

        margin-top: 6px;

        border-radius: 50%;

        background-color: #5b7aff;
      }

      &__label {
        font-weight: 500;
        color: #a5a5a5;
      }

      &__value {
        font-weight: 500;
        font-size: 16px;
        color: var(--expenses-main-text-color);

        span {
          font-size: 14px;
          margin-left: 3px;
        }
      }
    }
  }

  path {
    stroke: unset !important;
  }

  &__loader {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.wrap {
    .pie__content {
      flex-direction: column;

      &-chart {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
    .pie__header__text {
      display: flex;
      align-self: flex-start;
      align-items: center;
    }
    .pie__percentage {
      order: 2;
      display: inline;
      margin-left: 5px;
    }
    .pie__description {
      order: 1;
      margin-left: 0;
    }
    .pie__legend {
      gap: 0;
      overflow: hidden;

      &__by-params {
        background-color: initial;
        width: 50%;
        padding: 20px 13px;
      }
    }
  }
}

.column-chart__tooltip {
  padding: 10px 12px;
  color: #fff;
  backdrop-filter: blur(2px);

  &__label {
    font-size: 15px;
  }
}
.pie {
  .apexcharts-tooltip.apexcharts-theme-dark {
    background-color: initial !important;
  }
}
</style>
